/**
 * SnapLog PDF 보고서 생성기 (pdfmake 기반)
 *
 * 한글 폰트: pdfmake 기본 폰트로 처리 (한글은 이미지로 대체 불가하므로
 * 실제 프로덕션에서는 한글 폰트 vfs 필요. 현재 MVP에서는 영문+기본으로 처리)
 */

/**
 * pdfmake 동적 로딩
 */
let pdfMakeInstance = null;

async function getPdfMake() {
  if (pdfMakeInstance) return pdfMakeInstance;

  const pdfMake = await import('pdfmake/build/pdfmake');
  const pdfFonts = await import('pdfmake/build/vfs_fonts');

  pdfMake.default.vfs = pdfFonts.default.pdfMake ? pdfFonts.default.pdfMake.vfs : pdfFonts.default.vfs;
  pdfMakeInstance = pdfMake.default;
  return pdfMakeInstance;
}

/**
 * 학습 오답노트 PDF 생성
 */
export async function generateLearningPdf(record) {
  const pdfMake = await getPdfMake();

  const analysis = record.analysis || {};
  const content = [
    { text: 'SnapLog - 학습 분석 보고서', style: 'header' },
    { text: `생성일: ${new Date(record.created_at).toLocaleString('ko-KR')}`, style: 'date' },
    { text: ' ', margin: [0, 10] },

    // 기본 정보
    {
      table: {
        widths: [100, '*'],
        body: [
          [{ text: '레벨', bold: true }, record.level || '-'],
          [{ text: '과목', bold: true }, record.subject || '-'],
          [{ text: '난이도', bold: true }, record.difficulty || analysis.difficulty || '-'],
        ]
      },
      layout: 'lightHorizontalLines',
      margin: [0, 0, 0, 15],
    },
  ];

  // 문제
  const problem = record.problem_text || analysis.problem_text;
  if (problem) {
    content.push({ text: '문제', style: 'sectionHeader' });
    content.push({ text: problem, style: 'body', margin: [0, 5, 0, 15] });
  }

  // 내 답
  const studentAnswer = record.student_answer || analysis.student_answer;
  if (studentAnswer) {
    content.push({ text: '내 답', style: 'sectionHeader' });
    content.push({ text: studentAnswer, style: 'body', margin: [0, 5, 0, 15] });
  }

  // 정답
  const correctAnswer = record.correct_answer || analysis.correct_answer;
  if (correctAnswer) {
    content.push({ text: '정답', style: 'sectionHeader' });
    content.push({ text: correctAnswer, style: 'body', margin: [0, 5, 0, 15] });
  }

  // 해설
  const explanation = record.explanation || analysis.explanation;
  if (explanation) {
    content.push({ text: '해설', style: 'sectionHeader' });
    content.push({ text: explanation, style: 'body', margin: [0, 5, 0, 15] });
  }

  // 핵심 개념
  const keyConcept = record.key_concept || analysis.key_concept;
  if (keyConcept) {
    content.push({ text: '핵심 개념', style: 'sectionHeader' });
    content.push({ text: keyConcept, style: 'body', margin: [0, 5, 0, 15] });
  }

  const docDefinition = {
    content,
    styles: {
      header: { fontSize: 20, bold: true, margin: [0, 0, 0, 5] },
      date: { fontSize: 10, color: '#888' },
      sectionHeader: { fontSize: 14, bold: true, color: '#667eea', margin: [0, 5, 0, 3] },
      body: { fontSize: 11, lineHeight: 1.5 },
    },
    defaultStyle: { font: 'Roboto' },
  };

  return new Promise((resolve) => {
    pdfMake.createPdf(docDefinition).getBlob((blob) => {
      resolve(blob);
    });
  });
}

/**
 * 설비점검 보고서 PDF 생성 (법정 서식 기반)
 */
export async function generateInspectionPdf(record) {
  const pdfMake = await getPdfMake();
  const analysis = record.analysis || {};

  const content = [
    { text: 'SnapLog - 설비점검 보고서', style: 'header' },
    { text: `점검일: ${new Date(record.created_at).toLocaleString('ko-KR')}`, style: 'date' },
    { text: ' ', margin: [0, 10] },

    // 기본 정보 테이블
    {
      table: {
        widths: [120, '*'],
        body: [
          [{ text: '점검 유형', bold: true }, record.subject || '-'],
          [{ text: '설비 종류', bold: true }, analysis.equipment_type || '-'],
          [{ text: '제조사', bold: true }, analysis.manufacturer || '-'],
          [{ text: '모델', bold: true }, analysis.model || '-'],
          [{ text: '상태', bold: true }, analysis.condition || '-'],
          [{ text: '상태 점수', bold: true }, analysis.condition_score ? `${analysis.condition_score}/10` : '-'],
          ...(analysis.confidence ? [[{ text: '분석 신뢰도', bold: true }, `${Math.round((typeof analysis.confidence === 'string' ? parseFloat(analysis.confidence) : analysis.confidence) * 100)}%`]] : []),
        ]
      },
      layout: 'lightHorizontalLines',
      margin: [0, 0, 0, 15],
    },
  ];

  // 설명
  if (analysis.description) {
    content.push({ text: '상태 설명', style: 'sectionHeader' });
    content.push({ text: analysis.description, style: 'body', margin: [0, 5, 0, 15] });
  }

  // 체크리스트 테이블
  if (analysis.checklist && Array.isArray(analysis.checklist) && analysis.checklist.length > 0) {
    content.push({ text: '점검 체크리스트', style: 'sectionHeader' });
    content.push({
      table: {
        widths: ['*', 60, '*'],
        headerRows: 1,
        body: [
          [
            { text: '점검 항목', bold: true, fillColor: '#f0f0f0' },
            { text: '상태', bold: true, fillColor: '#f0f0f0', alignment: 'center' },
            { text: '비고', bold: true, fillColor: '#f0f0f0' },
          ],
          ...analysis.checklist.map(item => [
            item.item || '-',
            { text: item.status || '-', alignment: 'center', color: item.status === '양호' ? '#28a745' : item.status === '불량' ? '#dc3545' : '#856404' },
            item.note || '-',
          ]),
        ]
      },
      layout: 'lightHorizontalLines',
      margin: [0, 5, 0, 15],
    });
  }

  // 발견 문제 (긴급도별 분류)
  if (analysis.issues_found && analysis.issues_found.length > 0) {
    const hasUrgency = analysis.issues_found.some(i => typeof i === 'object' && i.urgency);

    if (hasUrgency) {
      const groups = { '즉시': [], '단기': [], '장기': [], '기타': [] };
      analysis.issues_found.forEach(issue => {
        if (typeof issue === 'string') {
          groups['기타'].push(issue);
        } else {
          const key = groups[issue.urgency] ? issue.urgency : '기타';
          groups[key].push(issue.item || issue);
        }
      });

      content.push({ text: '발견된 문제점 (긴급도별)', style: 'sectionHeader' });
      for (const [urgency, items] of Object.entries(groups)) {
        if (items.length === 0) continue;
        const urgColor = { '즉시': '#dc3545', '단기': '#e67e22', '장기': '#28a745', '기타': '#666' }[urgency];
        content.push({
          text: `[${urgency}]`,
          bold: true,
          color: urgColor,
          fontSize: 12,
          margin: [0, 5, 0, 2],
        });
        content.push({ ul: items, style: 'body', margin: [0, 0, 0, 8] });
      }
      content.push({ text: '', margin: [0, 0, 0, 7] });
    } else {
      content.push({ text: '발견된 문제점', style: 'sectionHeader' });
      content.push({
        ul: analysis.issues_found.map(i => typeof i === 'string' ? i : i.item || String(i)),
        style: 'body',
        margin: [0, 5, 0, 15],
      });
    }
  }

  // 권장 조치 (긴급도별 분류)
  if (analysis.recommendations && analysis.recommendations.length > 0) {
    const hasUrgency = analysis.recommendations.some(r => typeof r === 'object' && r.urgency);

    if (hasUrgency) {
      const groups = { '즉시': [], '단기': [], '장기': [], '기타': [] };
      analysis.recommendations.forEach(rec => {
        if (typeof rec === 'string') {
          groups['기타'].push(rec);
        } else {
          const key = groups[rec.urgency] ? rec.urgency : '기타';
          groups[key].push(rec.item || rec);
        }
      });

      content.push({ text: '권장 조치사항 (긴급도별)', style: 'sectionHeader' });
      for (const [urgency, items] of Object.entries(groups)) {
        if (items.length === 0) continue;
        const urgColor = { '즉시': '#dc3545', '단기': '#e67e22', '장기': '#28a745', '기타': '#666' }[urgency];
        content.push({
          text: `[${urgency}]`,
          bold: true,
          color: urgColor,
          fontSize: 12,
          margin: [0, 5, 0, 2],
        });
        content.push({ ul: items, style: 'body', margin: [0, 0, 0, 8] });
      }
      content.push({ text: '', margin: [0, 0, 0, 7] });
    } else {
      content.push({ text: '권장 조치사항', style: 'sectionHeader' });
      content.push({
        ul: analysis.recommendations.map(r => typeof r === 'string' ? r : r.item || String(r)),
        style: 'body',
        margin: [0, 5, 0, 15],
      });
    }
  }

  // 안전 우려
  if (analysis.safety_concerns) {
    content.push({ text: '안전 관련 우려사항', style: 'sectionHeader', color: '#e74c3c' });
    content.push({ text: analysis.safety_concerns, style: 'body', color: '#e74c3c', margin: [0, 5, 0, 15] });
  }

  // 법규 적합 여부 (소방)
  if (analysis.compliance) {
    content.push({ text: '법규 적합 여부', style: 'sectionHeader' });
    content.push({ text: analysis.compliance, style: 'body', margin: [0, 5, 0, 15] });
  }

  // 다음 점검
  if (analysis.next_inspection) {
    content.push({ text: '다음 점검 권장 시기', style: 'sectionHeader' });
    content.push({ text: analysis.next_inspection, style: 'body', margin: [0, 5, 0, 15] });
  }

  const docDefinition = {
    content,
    styles: {
      header: { fontSize: 20, bold: true, margin: [0, 0, 0, 5] },
      date: { fontSize: 10, color: '#888' },
      sectionHeader: { fontSize: 14, bold: true, color: '#e67e22', margin: [0, 5, 0, 3] },
      body: { fontSize: 11, lineHeight: 1.5 },
    },
    defaultStyle: { font: 'Roboto' },
  };

  return new Promise((resolve) => {
    pdfMake.createPdf(docDefinition).getBlob((blob) => {
      resolve(blob);
    });
  });
}

/**
 * 운동 자세 교정 PDF 생성
 */
export async function generateExercisePdf(record) {
  const pdfMake = await getPdfMake();
  const analysis = record.analysis || {};

  const content = [
    { text: 'SnapLog - 운동 자세 분석 보고서', style: 'header' },
    { text: `분석일: ${new Date(record.created_at).toLocaleString('ko-KR')}`, style: 'date' },
    { text: ' ', margin: [0, 10] },
    {
      table: {
        widths: [120, '*'],
        body: [
          [{ text: '운동 종류', bold: true }, analysis.exercise_type || '-'],
          [{ text: '자세 점수', bold: true }, analysis.posture_score ? `${analysis.posture_score}/10` : '-'],
          [{ text: '부상 위험도', bold: true }, analysis.injury_risk || '-'],
          ...(analysis.confidence ? [[{ text: '분석 신뢰도', bold: true }, `${Math.round(parseFloat(analysis.confidence) * 100)}%`]] : []),
        ]
      },
      layout: 'lightHorizontalLines',
      margin: [0, 0, 0, 15],
    },
  ];

  if (analysis.description) {
    content.push({ text: '자세 평가', style: 'sectionHeader' });
    content.push({ text: analysis.description, style: 'body', margin: [0, 5, 0, 15] });
  }

  if (analysis.good_points?.length) {
    content.push({ text: '잘하고 있는 점', style: 'sectionHeader' });
    content.push({ ul: analysis.good_points, style: 'body', margin: [0, 5, 0, 15] });
  }

  if (analysis.issues_found?.length) {
    content.push({ text: '자세 문제점', style: 'sectionHeader' });
    content.push({ ul: analysis.issues_found.map(i => typeof i === 'string' ? i : i.item || String(i)), style: 'body', margin: [0, 5, 0, 15] });
  }

  if (analysis.corrections?.length) {
    content.push({ text: '교정 방법', style: 'sectionHeader' });
    content.push({ ol: analysis.corrections, style: 'body', margin: [0, 5, 0, 15] });
  }

  if (analysis.recommendations?.length) {
    content.push({ text: '추가 권장사항', style: 'sectionHeader' });
    content.push({ ul: analysis.recommendations.map(r => typeof r === 'string' ? r : r.item || String(r)), style: 'body', margin: [0, 5, 0, 15] });
  }

  const docDefinition = {
    content,
    styles: {
      header: { fontSize: 20, bold: true, margin: [0, 0, 0, 5] },
      date: { fontSize: 10, color: '#888' },
      sectionHeader: { fontSize: 14, bold: true, color: '#e91e63', margin: [0, 5, 0, 3] },
      body: { fontSize: 11, lineHeight: 1.5 },
    },
    defaultStyle: { font: 'Roboto' },
  };

  return new Promise((resolve) => {
    pdfMake.createPdf(docDefinition).getBlob(resolve);
  });
}

/**
 * 건강검진 결과 PDF 생성
 */
export async function generateHealthPdf(record) {
  const pdfMake = await getPdfMake();
  const analysis = record.analysis || {};

  const content = [
    { text: 'SnapLog - 건강검진 분석 보고서', style: 'header' },
    { text: `분석일: ${new Date(record.created_at).toLocaleString('ko-KR')}`, style: 'date' },
    { text: '※ AI 참고용 분석입니다. 정확한 진단은 의사에게 문의하세요.', fontSize: 9, color: '#e67e22', margin: [0, 5, 0, 10] },
    {
      table: {
        widths: [120, '*'],
        body: [
          [{ text: '검진 유형', bold: true }, analysis.checkup_type || '-'],
          [{ text: '종합 판정', bold: true }, analysis.overall_status || '-'],
          ...(analysis.confidence ? [[{ text: '분석 신뢰도', bold: true }, `${Math.round(parseFloat(analysis.confidence) * 100)}%`]] : []),
        ]
      },
      layout: 'lightHorizontalLines',
      margin: [0, 0, 0, 15],
    },
  ];

  if (analysis.description) {
    content.push({ text: '건강 상태 요약', style: 'sectionHeader' });
    content.push({ text: analysis.description, style: 'body', margin: [0, 5, 0, 15] });
  }

  if (analysis.abnormal_items?.length) {
    content.push({ text: '이상/경계 항목', style: 'sectionHeader' });
    content.push({
      table: {
        widths: ['*', 60, 80, 50],
        headerRows: 1,
        body: [
          [
            { text: '항목', bold: true, fillColor: '#f0f0f0' },
            { text: '수치', bold: true, fillColor: '#f0f0f0' },
            { text: '참고치', bold: true, fillColor: '#f0f0f0' },
            { text: '상태', bold: true, fillColor: '#f0f0f0' },
          ],
          ...analysis.abnormal_items.map(item => [
            item.item,
            item.value || '-',
            item.reference || '-',
            { text: item.status || '-', color: item.status === '이상' ? '#dc3545' : '#856404' },
          ]),
        ]
      },
      layout: 'lightHorizontalLines',
      margin: [0, 5, 0, 15],
    });
  }

  if (analysis.lifestyle_advice?.length) {
    content.push({ text: '생활 습관 조언', style: 'sectionHeader' });
    content.push({ ul: analysis.lifestyle_advice, style: 'body', margin: [0, 5, 0, 15] });
  }

  if (analysis.follow_up?.length) {
    content.push({ text: '추가 검사 권장', style: 'sectionHeader' });
    content.push({ ul: analysis.follow_up, style: 'body', margin: [0, 5, 0, 15] });
  }

  const docDefinition = {
    content,
    styles: {
      header: { fontSize: 20, bold: true, margin: [0, 0, 0, 5] },
      date: { fontSize: 10, color: '#888' },
      sectionHeader: { fontSize: 14, bold: true, color: '#00bcd4', margin: [0, 5, 0, 3] },
      body: { fontSize: 11, lineHeight: 1.5 },
    },
    defaultStyle: { font: 'Roboto' },
  };

  return new Promise((resolve) => {
    pdfMake.createPdf(docDefinition).getBlob(resolve);
  });
}

/**
 * 범용 PDF 생성 (시나리오 타입에 따라 분기)
 */
export async function generatePdf(record) {
  const scenario_type = record.scenario_type;
  if (['hvac_inspection', 'fire_safety'].includes(scenario_type)) {
    return generateInspectionPdf(record);
  }
  if (scenario_type === 'exercise_coaching') {
    return generateExercisePdf(record);
  }
  if (scenario_type === 'health_checkup') {
    return generateHealthPdf(record);
  }
  return generateLearningPdf(record);
}

/**
 * PDF Blob을 다운로드
 */
export function downloadPdf(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'snaplog-report.pdf';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
